import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/atoms/Button';
import {
  MAX_CATEGORIES,
  MIN_CATEGORIES,
  PreferencesSelector,
  type PreferencesSelectorHandle,
} from '@/components/molecules/PreferencesSelector';
import { useOnboardingPreferencesTrigger } from '@/hooks/useOnboardingPreferencesTrigger';
import { assignInitialStories } from '@/services/api/stories.api';
import { useToast } from '@/context/ToastContext';
import { useStoryBottomBar } from '@/context/StoryBottomBarContext';
import styles from './OnboardingPreferencesPage.module.scss';

type FlowStep = 'select' | 'submitting' | 'error';

/**
 * Pantalla única para que el alumno declare sus categorías favoritas.
 * Layout calcado del de "Evolución del tiempo de estudio". El UI de
 * selección está extraído a `PreferencesSelector` para poder reusarlo
 * en el welcome onboarding (slide 7). El botón "Guardar" se publica
 * en la bottom bar global (mismo patrón que el intro de las historias)
 * en lugar de vivir dentro del contenido. La X del header se encarga
 * de cancelar.
 */
export function OnboardingPreferencesPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const editMode = searchParams.get('mode') === 'edit';

  const [step, setStep] = useState<FlowStep>('select');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isValid, setIsValid] = useState(false);

  const { complete, status } = useOnboardingPreferencesTrigger();
  // Si el alumno ya tiene temas guardados, el botón "actualiza"; si no, "guarda".
  const hasSavedInterests = editMode || !!status?.hasSavedCategories;
  const { toast } = useToast();
  const bottomBar = useStoryBottomBar();
  const selectorRef = useRef<PreferencesSelectorHandle>(null);

  const handleSave = async () => {
    const slugs = selectorRef.current?.getSelected() ?? [];
    if (!selectorRef.current?.isValid()) return;
    setStep('submitting');
    setErrorMessage(null);
    try {
      await complete(slugs);
      // Primera vez con nivel y sin asignaciones: siembra el primer lote para
      // que vea contenido ya. Idempotente; best-effort.
      try {
        await assignInitialStories();
      } catch (err) {
        console.error('assignInitialStories failed:', err);
      }
      toast({ description: t('onboardingPreferences.updatedToast'), variant: 'success' });
      // El toast confirma el guardado; el alumno se queda en la misma página.
      setStep('select');
    } catch (error) {
      console.error('Failed to save preferences:', error);
      setErrorMessage(
        error instanceof Error ? error.message : t('onboardingPreferences.errorGeneric'),
      );
      setStep('error');
    }
  };

  // Publica el botón "Guardar" en la bottom bar global mientras dura el
  // flujo de selección/submit/error. En 'done' o si hay error de
  // catálogo se vacía la barra. Cleanup obligatorio para no filtrar el
  // botón a la siguiente ruta del wrapper.
  const setBottomBarContent = bottomBar?.setContent;
  useEffect(() => {
    if (!setBottomBarContent) return;
    const isSubmitting = step === 'submitting';
    const isError = step === 'error';
    setBottomBarContent(
      <Button
        variant="primary"
        size="lg"
        fullWidth
        disabled={!isValid || isSubmitting}
        isLoading={isSubmitting}
        onClick={() => void handleSave()}
      >
        {isError
          ? t('onboardingPreferences.retry')
          : hasSavedInterests
            ? t('onboardingPreferences.update')
            : t('onboardingPreferences.save')}
      </Button>,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setBottomBarContent, step, isValid, t, hasSavedInterests]);

  useEffect(() => {
    return () => setBottomBarContent?.(null);
  }, [setBottomBarContent]);

  const title = editMode
    ? t('onboardingPreferences.editTitle')
    : t('onboardingPreferences.selectTitle');
  const subtitle = t('onboardingPreferences.selectDescription', {
    min: MIN_CATEGORIES,
    max: MAX_CATEGORIES,
  });

  return (
    <div className={styles.page}>
      <header className={styles.page__header}>
        <h1 className={styles.page__title}>{title}</h1>
        <p className={styles.page__subtitle}>{subtitle}</p>
      </header>

      <PreferencesSelector
        ref={selectorRef}
        disabled={step === 'submitting'}
        errorMessage={step === 'error' ? errorMessage : null}
        onValidityChange={setIsValid}
      />
    </div>
  );
}

export default OnboardingPreferencesPage;
